
import { useState, useEffect } from "react";
import { MessageCircle, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useCastResponses } from "@/hooks/useCastResponses";
import { supabase } from "@/integrations/supabase/client";
import { CastResponseInputModal } from "./CastResponseInputModal";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";

interface CastResponsesProps {
  castId: string;
  hideAddResponse?: boolean;
}

export const CastResponses = ({ castId, hideAddResponse = false }: CastResponsesProps) => {
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteResponseId, setDeleteResponseId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { responses, loading, createResponse, deleteResponse } = useCastResponses(castId);

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  const handleSubmitResponse = async (text: string) => {
    await createResponse(text);
  };

  const confirmDelete = async () => {
    if (deleteResponseId) {
      setIsDeleting(true);
      try {
        await deleteResponse(deleteResponseId);
      } finally {
        setIsDeleting(false);
        setDeleteResponseId(null);
      }
    }
  };

  const handleDeleteResponse = (responseId: string) => {
    setDeleteResponseId(responseId);
    setDeleteDialogOpen(true);
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return '1 day ago';
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="text-center py-4 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-xs mt-2">Loading responses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Add Response Button - Only show if not hidden */}
      {!hideAddResponse && (
        <div className="flex items-center justify-between p-2 bg-orange-50 rounded border border-orange-200">
          <div className="text-xs text-orange-800">
            <span className="font-medium">💬 Join the conversation</span> - Help out or ask questions
          </div>
          <Button
            onClick={() => setShowResponseModal(true)}
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white h-7 px-2 text-xs"
          >
            <MessageCircle size={12} className="mr-1" />
            Respond
          </Button>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && (
        <CastResponseInputModal
          onClose={() => setShowResponseModal(false)}
          onSubmit={handleSubmitResponse}
        />
      )}

      {/* Responses List */}
      <div className="space-y-2">
        {responses.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <MessageCircle className="mx-auto mb-1 text-gray-400" size={24} />
            <p className="text-xs">No responses yet</p>
            <p className="text-xs mt-1">Be the first to help out!</p>
          </div>
        ) : (
          responses.map((response) => (
            <div key={response.id} className="bg-gray-50 rounded p-3 border border-gray-200">
              {/* Response Header */}
              <div className="flex items-start space-x-2 mb-2">
                <Avatar className="w-6 h-6">
                  <AvatarImage 
                    src={response.profiles?.avatar_url || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"} 
                    alt={response.profiles?.farm_name || response.profiles?.username || "User"} 
                  />
                  <AvatarFallback>
                    {(response.profiles?.farm_name || response.profiles?.username || "U")[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1 text-xs">
                    <span className="font-medium text-gray-900 truncate">
                      {response.profiles?.farm_name || response.profiles?.username || "Anonymous User"}
                    </span>
                    <span className="text-gray-500 truncate">
                      @{response.profiles?.username || "unknown"}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-400 flex-shrink-0">
                      {formatTimestamp(response.created_at)}
                    </span>
                  </div>
                </div>
                {currentUserId === response.user_id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-0.5 h-auto">
                        <MoreHorizontal size={12} className="text-gray-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteResponse(response.id)}
                        className="text-red-600"
                      >
                        <Trash2 size={12} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Response Text */}
              <p className="text-sm text-gray-800 leading-relaxed">{response.response_text}</p>
            </div>
          ))
        )}
      </div>

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Response"
        description="Are you sure you want to delete this response? This action cannot be undone and the response cannot be restored."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
        loading={isDeleting}
      />
    </div>
  );
};
